import { useState } from "react";
import { QuotesIdSchema, type PecDocumentDetail } from "cadendar-shared";
import { DateTime } from "luxon";
import { Button } from "../../../components/ui/button";
import { trpc } from "../../main/components/trpc";
import { usePatientQuotes } from "../../quotes/useQuotes";

export default function PecDocumentQuoteLink({
  document,
}: {
  document: PecDocumentDetail;
}) {
  const [draft, setDraft] = useState<{
    quoteId: string | null;
    expectedQuoteId: PecDocumentDetail["quoteId"];
  } | null>(null);
  const utils = trpc.useUtils();
  const query = usePatientQuotes(document.patientId ?? undefined);
  const mutation = trpc.pec.linkPecDocumentQuote.useMutation({
    onSuccess: async () => {
      await Promise.all([
        utils.pec.getPecDocument.invalidate(document.id),
        utils.pec.listPecDocuments.invalidate(),
      ]);
      setDraft(null);
    },
  });
  if (!document.patientId) return null;
  const linkedQuote = query.data?.find(
    (quote) => quote.id === document.quoteId,
  );
  return (
    <section
      aria-label="Devis lié à la PEC"
      className="mb-4 rounded-lg border border-slate-200 bg-white p-4"
    >
      <h3 className="font-semibold text-slate-900">Devis lié</h3>
      <p className="mt-1 text-sm text-slate-600">
        {document.quoteId
          ? linkedQuote?.name || "Un devis est rattaché à ce document."
          : "Aucun devis lié à ce document."}
      </p>
      {draft ? (
        <form
          className="mt-3 space-y-3"
          onSubmit={(event) => {
            event.preventDefault();
            const quoteId = QuotesIdSchema.nullable().safeParse(draft.quoteId);
            if (quoteId.success)
              mutation.mutate({
                id: document.id,
                quoteId: quoteId.data,
                expectedQuoteId: draft.expectedQuoteId,
              });
          }}
        >
          <label className="block text-sm font-medium">
            Devis du patient
            <select
              className="mt-1 block w-full rounded border border-slate-300 bg-white p-2"
              value={draft.quoteId ?? ""}
              disabled={mutation.isPending || query.isPending || !!query.error}
              onChange={(event) =>
                setDraft({ ...draft, quoteId: event.target.value || null })
              }
            >
              <option value="">Aucun devis</option>
              {query.data?.map((quote) => (
                <option key={quote.id} value={quote.id}>
                  {quote.name} ·{" "}
                  {DateTime.fromJSDate(quote.creation_date).toFormat(
                    "dd/MM/yyyy",
                  )}
                </option>
              ))}
            </select>
          </label>
          <p className="text-xs text-slate-500">
            Vérifiez que le document concerne bien ce devis. Le rattachement ne
            vaut pas accord de prise en charge.
          </p>
          {query.error ? (
            <p role="alert" className="text-sm text-red-700">
              Devis indisponibles.{" "}
              <button
                type="button"
                className="underline"
                onClick={() => void query.refetch()}
              >
                Réessayer
              </button>
            </p>
          ) : null}
          {mutation.error ? (
            <p role="alert" className="text-sm text-red-700">
              {mutation.error.message}
            </p>
          ) : null}
          <div className="flex gap-2">
            <Button
              type="submit"
              disabled={mutation.isPending || query.isPending || !!query.error}
            >
              {mutation.isPending
                ? "Enregistrement…"
                : "Enregistrer le rattachement"}
            </Button>
            <Button
              type="button"
              variant="outline"
              disabled={mutation.isPending}
              onClick={() => setDraft(null)}
            >
              Annuler
            </Button>
          </div>
        </form>
      ) : (
        <Button
          type="button"
          variant="outline"
          className="mt-3"
          onClick={() => {
            mutation.reset();
            setDraft({
              quoteId: document.quoteId,
              expectedQuoteId: document.quoteId,
            });
          }}
        >
          {document.quoteId
            ? "Modifier le rattachement"
            : "Rattacher à un devis"}
        </Button>
      )}
    </section>
  );
}
